import type { AiBreakdown } from '@/api/types';
import { BarList, SERIES } from '@/features/analytics/charts';
import { formatNumber, formatTokens, humanize } from '@/lib/format';
import { AI_FEATURE_LABEL, failureRate, providerLabel } from './meta';

/** Requests per AI provider with share, reach (clients), latency and failure rate. */
export function AiProvidersTable({
  rows,
  showClients = true,
}: {
  rows: AiBreakdown['byProvider'];
  showClients?: boolean;
}) {
  if (rows.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-slate-400">
        No AI requests in this period. Usage is recorded as clients use AI replies, summaries and bots.
      </p>
    );
  }
  const total = rows.reduce((s, r) => s + r.requests, 0);
  const max = Math.max(1, ...rows.map((r) => r.requests));
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[600px] text-left text-sm">
        <thead className="text-xs font-semibold tracking-wide text-slate-500 uppercase">
          <tr className="border-b border-slate-100">
            <th scope="col" className="py-2 pr-3">
              Provider
            </th>
            <th scope="col" className="w-2/5 py-2 pr-3">
              Requests
            </th>
            {showClients && (
              <th scope="col" className="py-2 pr-3 text-right">
                Clients
              </th>
            )}
            <th scope="col" className="py-2 pr-3 text-right">
              Tokens
            </th>
            <th scope="col" className="py-2 pr-3 text-right">
              Avg latency
            </th>
            <th scope="col" className="py-2 text-right">
              Failed
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r) => {
            const fr = failureRate(r.requests, r.failed);
            return (
              <tr key={r.provider}>
                <td className="py-2.5 pr-3 font-medium text-slate-900">{providerLabel(r.provider)}</td>
                <td className="py-2.5 pr-3">
                  <div className="flex items-center gap-2">
                    <div className="h-2 flex-1 rounded-full bg-slate-100">
                      <div
                        className="h-2 rounded-full"
                        style={{ width: `${Math.max(2, (r.requests / max) * 100)}%`, background: SERIES[0] }}
                      />
                    </div>
                    <span className="w-24 shrink-0 text-right text-xs text-slate-500 tabular-nums">
                      <span className="font-semibold text-slate-900">{formatNumber(r.requests)}</span> ·{' '}
                      {Math.round((r.requests / total) * 100)}%
                    </span>
                  </div>
                </td>
                {showClients && <td className="py-2.5 pr-3 text-right tabular-nums">{r.clients}</td>}
                <td
                  className="py-2.5 pr-3 text-right text-slate-600 tabular-nums"
                  title={`${formatNumber(r.inputTokens)} in · ${formatNumber(r.outputTokens)} out`}
                >
                  {formatTokens(r.inputTokens + r.outputTokens)}
                </td>
                <td className="py-2.5 pr-3 text-right text-slate-600 tabular-nums">
                  {(r.avgLatencyMs / 1000).toFixed(1)}s
                </td>
                <td className="py-2.5 text-right tabular-nums">
                  <span className={fr != null && fr >= 5 ? 'font-semibold text-amber-700' : 'text-slate-600'}>
                    {fr == null ? '—' : `${fr}%`}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** What the AI is used for (features) and which models answer most. */
export function AiUsageBreakdown({ ai }: { ai: AiBreakdown }) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-3 text-sm font-semibold text-slate-900">Used for</h3>
        <BarList
          rows={ai.byFeature.map((f) => ({
            label: AI_FEATURE_LABEL[f.feature] ?? humanize(f.feature),
            value: f.requests,
          }))}
        />
      </div>
      <div>
        <h3 className="mb-3 text-sm font-semibold text-slate-900">Tokens by feature</h3>
        <BarList
          rows={ai.byFeature
            .filter((f) => f.inputTokens + f.outputTokens > 0)
            .map((f) => ({
              label: AI_FEATURE_LABEL[f.feature] ?? humanize(f.feature),
              value: f.inputTokens + f.outputTokens,
            }))}
        />
      </div>
      <div>
        <h3 className="mb-3 text-sm font-semibold text-slate-900">Top models</h3>
        {ai.byModel.length === 0 ? (
          <p className="text-sm text-slate-400">No data for this period</p>
        ) : (
          <ul className="divide-y divide-slate-100 text-sm">
            {ai.byModel.map((m) => (
              <li key={`${m.provider}/${m.model}`} className="flex items-center justify-between gap-3 py-2">
                <span className="min-w-0">
                  <span className="block truncate font-mono text-xs text-slate-900">{m.model}</span>
                  <span className="text-xs text-slate-500">{providerLabel(m.provider)}</span>
                </span>
                <span className="shrink-0 text-right tabular-nums">
                  <span className="block font-semibold text-slate-900">{formatNumber(m.requests)}</span>
                  <span className="text-xs text-slate-500">
                    {formatTokens(m.inputTokens + m.outputTokens)} tokens
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
